import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { configureAuth } from './config/authConfig';
import { getEnvironmentInfo } from './config/amplifyConfig';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingScreen from './components/common/LoadingScreen';
import Sidebar from './components/common/Sidebar';
import AppHeader from './components/common/AppHeader';
import NotificationCenter from './components/common/NotificationCenter';
import { ToastProvider } from './components/common/Toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { MoodProvider } from './contexts/MoodContext';
import { ThemeProvider } from './contexts/ThemeContext';
import MoodMatePage from './components/moodmate/MoodMatePage';
import MoodCheckInGate from './components/moodmate/MoodCheckInGate';
import { MOODMATE_ENABLED } from './config/features';
import InactivityGuard from './components/common/InactivityGuard';
import { errorHandler } from './services/errorHandler';
import Login from './components/auth/LoginForm';
import ManagerDashboard from './components/dashboard/ManagerDashboard';
import AdminDashboard from './components/dashboard/AdminDashboard';
import SuperAdminDashboard from './components/dashboard/SuperAdminDashboard';
import ExpenseManager from './components/expenses/ExpenseManager';
import MyExpensesPage from './components/expenses/MyExpensesPage';
import SettingsPage from './components/settings/SettingsPage';
import MyInformation from './components/employee/MyInformation';
import MyTimesheet from './components/timesheet/MyTimesheet';
import TeamTimesheets from './components/timesheet/TeamTimesheets';
import ProjectsManager from './components/timesheet/ProjectsManager';
import LeaveDashboardPage from './components/leave/pages/LeaveDashboardPage';
import ApplyLeavePage from './components/leave/pages/ApplyLeavePage';
import TeamLeaveMatrixPage from './components/leave/pages/TeamLeaveMatrixPage';
import LeaveApprovalsPage from './components/leave/pages/LeaveApprovalsPage';
import LeavePoliciesPage from './components/leave/pages/LeavePoliciesPage';
import HROperationsPage from './components/leave/pages/HROperationsPage';
import LeaveReportsPage from './components/leave/pages/LeaveReportsPage';
import ApplyCompOffPage from './components/leave/pages/ApplyCompOffPage';
import LeaveCancellationPage from './components/leave/pages/LeaveCancellationPage';
import ShiftRosterPage from './components/shift-roster/ShiftRosterPage';
import AttendancePage from './components/attendance/AttendancePage';
import HomePage from './components/pages/HomePage';
import CompleteProfilePage from './components/onboarding/CompleteProfilePage';
import { UserRole } from './types';
import {
  canViewTeamExpenses,
  canViewAllExpenses,
  canManageUsers,
  canAccessShiftRoster,
  canViewShiftRoster
} from './utils/rolePermissions';
import {
  canCreateNomination,
  canReviewNomination,
  canManageAllNominations
} from './utils/nominationPermissions';
import { useNominationRole } from './hooks/useNominationRole';
import AllCertificationsPage from './components/certifications/AllCertificationsPage';
import MyCertificationsPage from './components/certifications/MyCertificationsPage';
import CertificationReimbursementPage from './components/certifications/CertificationReimbursementPage';
import CertificationApprovals from './components/certifications/CertificationApprovals';
import NominationFeed from './components/nominations/NominationFeed';
import CreateNomination from './components/nominations/CreateNomination';
import NominationList from './components/nominations/NominationList';
import NominationDetail from './components/nominations/NominationDetail';
import ReviewQueue from './components/nominations/ReviewQueue';
import NominationAdmin from './components/nominations/NominationAdmin';
import VotingPolls from './components/voting/VotingPolls';
import CreateVotingPoll from './components/voting/CreateVotingPoll';
import VotingPollDetail from './components/voting/VotingPollDetail';
import ChatWidget from './components/chatbot/ChatWidget';
import ChatFullPage from './components/chatbot/ChatFullPage';
import {
  PMSEmployeeDashboardRoute,
  PMSObjectiveFormRoute,
  PMSObjectiveDetailRoute,
  PMSReviewDetailRoute,
  PMSManagerDashboardRoute,
  PMSObjectiveListRoute,
  PMSReviewFormRoute,
  PMSHRAdminDashboardRoute,
  PMSReviewCycleConfigRoute,
  PMSPerformanceReportsRoute,
  PMSObjectiveLockManagerRoute,
} from './components/performance/PMSRouteWrappers';
import AnnualObjectiveCycle from './components/performance/AnnualObjectiveCycle';
import {
  mapUserRoleToPMSRole,
  canCreateObjective,
  canApproveObjective,
  canConfigureReviewCycle
} from './utils/pmsPermissions';
import './App.css';
import './components/common/ErrorBoundary.css';

// Configure Amplify with OAuth support for Azure AD SSO
configureAuth();

// Log environment info in development
if (process.env.NODE_ENV === 'development') {
  console.log('[App] Environment:', getEnvironmentInfo());
}

// Check if this is an OAuth callback (has code parameter)
const isOAuthCallback = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('code');
};

// Protected Route component with role inheritance support
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: UserRole[];
  requiredPermission?: 'team' | 'finance' | 'admin' | 'shift-roster' | 'shift-roster-view';
  nominationPermission?: 'manager' | 'reviewer' | 'admin';
  pmsPermission?: 'employee' | 'manager' | 'hr_admin';
}> = ({
  children,
  allowedRoles,
  requiredPermission,
  nominationPermission,
  pmsPermission
}) => {
    const routeLocation = useLocation();
    const renderStart = performance.now();
    const { user, isAuthenticated, isLoading } = useAuth();
    const { nominationRole, isLoading: nominationSettingsLoading } = useNominationRole(
      nominationPermission ? user : null
    );
    const bypassNominationSettingsLoading = Boolean(
      nominationPermission && user?.role === UserRole.SUPER_ADMIN
    );

    useEffect(() => {
      if (bypassNominationSettingsLoading && nominationSettingsLoading) {
        console.log('[Perf][ProtectedRoute][Bypass]', {
          path: routeLocation.pathname,
          nominationPermission,
          userRole: user?.role,
          reason: 'SUPER_ADMIN system role already grants nomination admin access',
        });
      }
      console.log('[Perf][ProtectedRoute]', {
        path: routeLocation.pathname,
        durationMs: Math.round(performance.now() - renderStart),
        nominationPermission,
        nominationRole,
        nominationSettingsLoading,
        bypassNominationSettingsLoading,
        isAuthenticated,
        isLoading,
        userEmail: user?.email,
      });
      console.log('[Perf][NominationRole] ProtectedRoute render complete', {
        path: routeLocation.pathname,
        durationMs: Math.round(performance.now() - renderStart),
        nominationPermission,
        nominationRole,
        nominationSettingsLoading,
        bypassNominationSettingsLoading,
        isAuthenticated,
        isLoading,
        userEmail: user?.email,
      });
    });

    // Don't redirect during OAuth callback - let AuthContext handle the code exchange
    if (isOAuthCallback()) {
      return <LoadingScreen message="Completing sign in..." />;
    }

    if (isLoading) {
      return <LoadingScreen message="Loading your dashboard" />;
    }

    if (nominationPermission && nominationSettingsLoading && !bypassNominationSettingsLoading) {
      console.log('[Perf][NominationRole] ProtectedRoute showing nomination access loader', {
        path: routeLocation.pathname,
        nominationPermission,
        nominationRole,
      });
      return <LoadingScreen message="Loading nomination access" />;
    }

    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    // Check permission-based access (uses role inheritance)
    if (requiredPermission && user) {
      let hasAccess = false;
      switch (requiredPermission) {
        case 'team':
          hasAccess = canViewTeamExpenses(user.role);
          break;
        case 'finance':
          hasAccess = canViewAllExpenses(user.role);
          break;
        case 'admin':
          hasAccess = canManageUsers(user.role);
          break;
        case 'shift-roster':
          hasAccess = canAccessShiftRoster(user.role);
          break;
        // Viewing Shift Details is open to everyone; editing is still gated by
        // 'shift-roster' above and by requireRosterAccess in the backend.
        case 'shift-roster-view':
          hasAccess = canViewShiftRoster(user.role);
          break;
      }
      if (!hasAccess) {
        return <Navigate to="/dashboard" replace />;
      }
    }

    // Check nomination permission (maps EMS role to nomination role)
    if (nominationPermission && user) {
      let hasAccess = false;
      switch (nominationPermission) {
        case 'manager':
          hasAccess = canCreateNomination(nominationRole);
          break;
        case 'reviewer':
          hasAccess = canReviewNomination(nominationRole);
          break;
        case 'admin':
          hasAccess = canManageAllNominations(nominationRole);
          break;
      }
      if (!hasAccess) {
        return <Navigate to="/nominations/feed" replace />;
      }
    }

    // Check PMS permission (maps EMS role to PMS role)
    if (pmsPermission && user) {
      const pmsRole = mapUserRoleToPMSRole(user.role);
      let hasAccess = false;
      switch (pmsPermission) {
        case 'employee':
          hasAccess = canCreateObjective(pmsRole);
          break;
        case 'manager':
          hasAccess = canApproveObjective(pmsRole);
          break;
        case 'hr_admin':
          hasAccess = canConfigureReviewCycle(pmsRole);
          break;
      }
      if (!hasAccess) {
        return <Navigate to="/performance/objectives" replace />;
      }
    }

    // Legacy role-based check (for backward compatibility)
    if (allowedRoles && user && !allowedRoles.includes(user.role)) {
      return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
  };

// Layout with Sidebar for authenticated pages
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isResponsiveSidebar, setIsResponsiveSidebar] = useState(false);
  const [isOverlaySidebar, setIsOverlaySidebar] = useState(false);
  const location = useLocation();
  const showAppHeader = location.pathname !== '/home' && location.pathname !== '/chat';
  const showNotificationCenter = location.pathname === '/home';

  useEffect(() => {
    const applyResponsiveSidebar = () => {
      const width = window.innerWidth;
      const shouldOverlay = width <= 1200;
      const shouldCollapse = width <= 1440;

      setIsResponsiveSidebar(shouldCollapse);
      setIsOverlaySidebar(shouldOverlay);

      if (shouldCollapse) {
        setSidebarCollapsed(true);
        return;
      }

      const savedState = localStorage.getItem('sidebarCollapsed');
      setSidebarCollapsed(savedState !== null ? JSON.parse(savedState) : false);
    };

    applyResponsiveSidebar();
    window.addEventListener('resize', applyResponsiveSidebar);
    return () => window.removeEventListener('resize', applyResponsiveSidebar);
  }, []);

  // Load sidebar state from localStorage
  useEffect(() => {
    if (isResponsiveSidebar) return;
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setSidebarCollapsed(JSON.parse(savedState));
    }
  }, [isResponsiveSidebar]);

  // Save sidebar state to localStorage
  const handleSidebarToggle = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    if (!isResponsiveSidebar) {
      localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
    }
  };

  return (
    <div className={`app-layout ${isOverlaySidebar ? 'app-layout--overlay-sidebar' : ''}`}>
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
      <main className={`main-content-area ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${showAppHeader ? 'has-app-header' : ''}`}>
        {showAppHeader && <AppHeader />}
        {showNotificationCenter && <NotificationCenter />}
        {children}
      </main>
    </div>
  );
};

// Dashboard router — always shows MyExpensesPage (personal expenses)
// Role-specific dashboards are at /manager, /admin, /super-admin
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <MyExpensesPage />;
};

function AppContent() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const handleGlobalError = (event: ErrorEvent) => {
      errorHandler.handleError(event.error);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      errorHandler.handleError(event.reason);
    };

    window.addEventListener('error', handleGlobalError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleGlobalError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />

          {/*
            * PUBLIC. The only route besides /login with no ProtectedRoute around it.
            *
            * Deliberate: the person opening this has just been hired and may never have signed
            * in. Requiring SSO first would make the invitation useless for exactly the people it
            * exists for, and there is nothing here for a session to authorise anyway — the token
            * in the path is what the server checks, and it resolves to one employee's record.
            *
            * No AuthenticatedLayout either. A sidebar full of links a stranger cannot open is
            * noise at best, and at worst it implies the page is part of an app they have access
            * to. The page carries its own styling (completeProfile.css).
            */}
          <Route path="/complete-profile/:token" element={<CompleteProfilePage />} />
          
          {/* Chat Full Page Route */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ChatFullPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <HomePage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <DashboardRouter />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyExpensesPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/my"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyExpensesPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/new"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ExpenseManager mode="create" />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/:id"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ExpenseManager mode="view" />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/expenses/:id/edit"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ExpenseManager mode="edit" />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredPermission="finance">
                <AuthenticatedLayout>
                  <AdminDashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin"
            element={
              <ProtectedRoute requiredPermission="admin">
                <AuthenticatedLayout>
                  <SuperAdminDashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requiredPermission="admin">
                <AuthenticatedLayout>
                  <SettingsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/*
            * Your own submitted employee information.
            *
            * No requiredPermission: it is every signed-in person's own record, and the SERVER
            * decides whose — getEmployeeSelfServiceProfile reads the caller's identity from the
            * pool and refuses any address but theirs. A permission here would be a second,
            * weaker statement of a rule that is already enforced where it matters.
            */}
          <Route
            path="/my-information"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyInformation />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* Sub-sections of My Information (e.g. the personal Mood Journal) live
              under a URL tab so they survive a refresh, like Attendance. */}
          <Route
            path="/my-information/:tab"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyInformation />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manager"
            element={
              <ProtectedRoute requiredPermission="team">
                <AuthenticatedLayout>
                  <ManagerDashboard />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/home" replace />} />

          {/* Nomination Routes */}
          {/*
            * Certifications: TWO pages, two routes.
            *
            * Page 1 is applying and tracking your own reimbursement claims; Page 2 is the
            * repository of qualifications people hold. They are separate destinations rather
            * than tabs of one screen because they answer different questions and are visited
            * for different reasons — and the sidebar lists them as siblings to match.
            *
            * ProtectedRoute with no extra permission on either: ANY authenticated employee may
            * claim a certification and record their own. There is no nomination requirement and
            * no Settings configuration to be named in — those gate the APPROVAL, not the record.
            * What each viewer may see is decided inside each page, which reads only its own rows
            * for anybody who cannot see the organisation's.
            */}
          {/* Page 1 — Apply for Reimbursement. */}
          <Route
            path="/certifications/reimbursement"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CertificationReimbursementPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* Page 2 — All Certifications, the repository. */}
          <Route
            path="/certifications/all"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <AllCertificationsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* My Certifications — the signed-in employee's own records, both routes in one list:
              reimbursement claims (with their approval status) and self-recorded certifications.
              ProtectedRoute only, like the other two — it reads only the viewer's own rows. */}
          <Route
            path="/certifications/mine"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyCertificationsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* The bare module path lands on Page 1, matching the sidebar's first child. */}
          <Route path="/certifications" element={<Navigate to="/certifications/reimbursement" replace />} />
          {/*
            * Certification approvals.
            *
            * ProtectedRoute only — the page itself decides, through useCertificationApproval,
            * and refuses anyone without the right. Gating by ROLE here would be wrong in both
            * directions: it would let in an admin who does not need this page, and shut out the
            * configured Certification Admin, who is an ordinary employee by role and is exactly
            * who the page exists for.
            */}
          {/* Three routes, one component. Each approver gets their own place to work, and the
              queue, the claim card and the ordering rules exist once — see the note on
              CertificationApprovalsProps. Role is checked inside, per stage. */}
          <Route
            path="/certifications/approvals"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CertificationApprovals stage="manager" />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/certifications/admin-approvals"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CertificationApprovals stage="admin" />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/certifications/finance"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <CertificationApprovals stage="finance" />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominations/feed"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <NominationFeed />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominations/my"
            element={
              <ProtectedRoute nominationPermission="manager">
                <AuthenticatedLayout>
                  <NominationList />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominations/new"
            element={
              <ProtectedRoute nominationPermission="manager">
                <AuthenticatedLayout>
                  <CreateNomination />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominations/review"
            element={
              <ProtectedRoute nominationPermission="reviewer">
                <AuthenticatedLayout>
                  <ReviewQueue />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominations/admin"
            element={
              <ProtectedRoute nominationPermission="admin">
                <AuthenticatedLayout>
                  <NominationAdmin />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominations/:id/edit"
            element={
              <ProtectedRoute nominationPermission="manager">
                <AuthenticatedLayout>
                  <CreateNomination />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/nominations/:id"
            element={
              <ProtectedRoute nominationPermission="manager">
                <AuthenticatedLayout>
                  <NominationDetail />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Voting Poll Routes */}
          <Route
            path="/voting"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <VotingPolls />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/voting/new"
            element={
              <ProtectedRoute nominationPermission="reviewer">
                <AuthenticatedLayout>
                  <CreateVotingPoll />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/voting/:id"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <VotingPollDetail />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Timesheet Routes */}
          <Route
            path="/timesheet"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <MyTimesheet />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheet/team"
            element={
              <ProtectedRoute requiredPermission="team">
                <AuthenticatedLayout>
                  <TeamTimesheets />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/timesheet/projects"
            element={
              <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.HR_ADMIN]}>
                <AuthenticatedLayout>
                  <ProjectsManager />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Leave Management Routes */}
          <Route path="/leave" element={<Navigate to="/leave/dashboard" replace />} />
          <Route
            path="/leave/dashboard"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <LeaveDashboardPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/apply"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ApplyLeavePage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* Comp-Off and Cancellation are open to every authenticated user: the pages
              themselves scope what is editable (HR/Super Admin manage grants; a manager
              sees their reports' cancellations) rather than gating the whole route. */}
          <Route
            path="/leave/comp-off"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ApplyCompOffPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/cancellation"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <LeaveCancellationPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* The manager Grant Comp-Off page is gone, and its replacement is not a route: Comp-Off
              History is a tab on Apply Comp-Off, which already loads the direct-reports list, the
              directory and the Comp-Off records it needs. Both old paths redirect rather than 404 —
              either may be bookmarked, and Apply Comp-Off is where what they wanted now lives. */}
          <Route
            path="/leave/manager-grant"
            element={<Navigate to="/leave/comp-off" replace />}
          />
          <Route
            path="/leave/comp-off-history"
            element={<Navigate to="/leave/comp-off" replace />}
          />
          <Route
            path="/leave/team-matrix"
            element={
              <ProtectedRoute allowedRoles={[UserRole.MANAGER, UserRole.HR_ADMIN, UserRole.SUPER_ADMIN]}>
                <AuthenticatedLayout>
                  <TeamLeaveMatrixPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/approvals"
            element={
              <ProtectedRoute allowedRoles={[UserRole.MANAGER, UserRole.HR_ADMIN, UserRole.SUPER_ADMIN, UserRole.FINANCE_ADMIN]}>
                <AuthenticatedLayout>
                  <LeaveApprovalsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/policies"
            element={
              <ProtectedRoute allowedRoles={[UserRole.HR_ADMIN, UserRole.SUPER_ADMIN]}>
                <AuthenticatedLayout>
                  <LeavePoliciesPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/hr-operations"
            element={
              <ProtectedRoute allowedRoles={[UserRole.HR_ADMIN, UserRole.SUPER_ADMIN]}>
                <AuthenticatedLayout>
                  <HROperationsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/reports"
            element={
              <ProtectedRoute allowedRoles={[UserRole.HR_ADMIN, UserRole.SUPER_ADMIN]}>
                <AuthenticatedLayout>
                  <LeaveReportsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Shift Roster Routes */}
          <Route
            path="/shift-roster"
            element={<Navigate to="/shift-roster/shift-details" replace />}
          />
          {/* Read-only for Employee, editable for Manager and above — the page gates its own
              controls on canAccessShiftRoster. Bulk Override below stays on 'shift-roster'
              because it exists only to write. */}
          <Route
            path="/shift-roster/shift-details"
            element={
              <ProtectedRoute requiredPermission="shift-roster-view">
                <AuthenticatedLayout>
                  <ShiftRosterPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* Not requiredPermission="shift-roster" like its siblings: that maps to
              canAccessShiftRoster, which includes Manager. Creating and editing shift
              definitions is canManageShiftMaster — HR and Super Admin only — and the page's
              own `activeTab === 'config' && canConfig` gate already agreed. Without this a
              Manager could reach the URL directly and get a page with a heading and no body. */}
          <Route
            path="/shift-roster/shift-configuration"
            element={
              <ProtectedRoute allowedRoles={[UserRole.HR_ADMIN, UserRole.SUPER_ADMIN]}>
                <AuthenticatedLayout>
                  <ShiftRosterPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/shift-roster/bulk-override"
            element={
              <ProtectedRoute requiredPermission="shift-roster">
                <AuthenticatedLayout>
                  <ShiftRosterPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          {/* Reports is HR_Admin / Super_Admin only — stricter than the roster it reads, because
              it exports the whole directory in one file (see canAccessShiftReports). Spelled with
              allowedRoles for the same reason Shift Configuration is: 'shift-roster' maps to
              canAccessShiftRoster, which would let a Manager in. */}
          <Route
            path="/shift-roster/reports"
            element={
              <ProtectedRoute allowedRoles={[UserRole.HR_ADMIN, UserRole.SUPER_ADMIN]}>
                <AuthenticatedLayout>
                  <ShiftRosterPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Attendance Routes
              One page, tabbed, with the tab in the URL so a link to Device Sync survives a
              refresh. Deliberately NOT role-gated at the route: every employee has attendance,
              and the page itself hides the admin tabs and each admin pane re-checks the role.
              Gating the route to admins would lock employees out of their own attendance. */}
          <Route path="/attendance" element={<Navigate to="/attendance/my-attendance" replace />} />
          <Route
            path="/attendance/:tab"
            element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <AttendancePage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* Performance Management Routes */}
          <Route
            path="/performance/objectives"
            element={
              <ProtectedRoute pmsPermission="employee">
                <AuthenticatedLayout>
                  <PMSEmployeeDashboardRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/objectives/new"
            element={
              <ProtectedRoute pmsPermission="employee">
                <AuthenticatedLayout>
                  <PMSObjectiveFormRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/objectives/:id"
            element={
              <ProtectedRoute pmsPermission="employee">
                <AuthenticatedLayout>
                  <PMSObjectiveDetailRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/reviews"
            element={
              <ProtectedRoute pmsPermission="employee">
                <AuthenticatedLayout>
                  <PMSReviewDetailRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/team"
            element={
              <ProtectedRoute pmsPermission="manager">
                <AuthenticatedLayout>
                  <PMSManagerDashboardRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/team/:employeeId"
            element={
              <ProtectedRoute pmsPermission="manager">
                <AuthenticatedLayout>
                  <PMSObjectiveListRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/approvals"
            element={
              <ProtectedRoute pmsPermission="manager">
                <AuthenticatedLayout>
                  <PMSObjectiveListRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/team-reviews"
            element={
              <ProtectedRoute pmsPermission="manager">
                <AuthenticatedLayout>
                  <PMSReviewFormRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/team-reviews/:empId"
            element={
              <ProtectedRoute pmsPermission="manager">
                <AuthenticatedLayout>
                  <PMSReviewFormRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/annual-cycle"
            element={
              <ProtectedRoute pmsPermission="hr_admin">
                <AuthenticatedLayout>
                  <AnnualObjectiveCycle />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/analytics"
            element={
              <ProtectedRoute pmsPermission="hr_admin">
                <AuthenticatedLayout>
                  <PMSHRAdminDashboardRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/review-cycles"
            element={
              <ProtectedRoute pmsPermission="hr_admin">
                <AuthenticatedLayout>
                  <PMSReviewCycleConfigRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/reports"
            element={
              <ProtectedRoute pmsPermission="hr_admin">
                <AuthenticatedLayout>
                  <PMSPerformanceReportsRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/performance/lock-management"
            element={
              <ProtectedRoute pmsPermission="hr_admin">
                <AuthenticatedLayout>
                  <PMSObjectiveLockManagerRoute />
                </AuthenticatedLayout>
              </ProtectedRoute>
            }
          />

          {/* MoodMate — optional mood-based personalized dashboard.
              Disabled per-environment via REACT_APP_ENABLE_MOODMATE=false (e.g. prod):
              every MoodMate path then redirects home instead of mounting the feature. */}
          {MOODMATE_ENABLED ? (
            <>
              <Route
                path="/moodmate"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <MoodMatePage />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              {/* The Mood Journal now lives under My Information; keep the old path
                  working by redirecting any bookmarks to its new home. */}
              <Route path="/moodmate/journal" element={<Navigate to="/my-information/mood-journal" replace />} />
            </>
          ) : (
            <Route path="/moodmate/*" element={<Navigate to="/home" replace />} />
          )}

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>

        {/* Chatbot Widget - Only show when authenticated and NOT on /chat page */}
        <ConditionalChatWidget />

        {/* MoodMate once-per-session check-in (self-gates; respects user prefs).
            Hidden entirely where the feature is disabled (e.g. prod). */}
        {MOODMATE_ENABLED && <MoodCheckInGate />}

        {/* App-wide session inactivity timeout (self-gates on auth + admin policy). */}
        <InactivityGuard />
      </div>
    </Router>
  );
}

// Conditional ChatWidget - only show when not on /chat page
const ConditionalChatWidget: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  
  // Don't show widget on /chat page (full page chat)
  if (location.pathname === '/chat') {
    return null;
  }
  
  // Only show when authenticated
  if (!isAuthenticated) {
    return null;
  }
  
  return <ChatWidget />;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <NotificationProvider>
              <MoodProvider>
                <AppContent />
              </MoodProvider>
            </NotificationProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
